import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Mail, Lock, User, ArrowUpRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { LOGO_MARK, TAGLINE } from "../lib/brand";
import { LogoMark } from "../components/Logo";

export default function Register() {
  const { register, user, loading } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register(form.email, form.password, form.name);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Google OAuth is not available in standalone mode.

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-2 mb-8" data-testid="logo-link">
          <LogoMark src={LOGO_MARK} size="w-16 h-16" className="animate-float" />
          <span className="brand-text text-2xl">NORDGAMING</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{TAGLINE}</span>
        </Link>

        <div className="card p-8 fade-in tracing-border">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary mb-3">02 · Register</div>
          <h1 className="font-display text-3xl tracking-tight mb-2">Werde Mitglied</h1>
          <p className="text-white/60 text-sm mb-8">
            Erstelle in weniger als einer Minute deinen Account und erhalte deinen Sportlerausweis.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label">Name</label>
              <div className="relative">
                <User className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Max Mustermann"
                  className="input pl-10"
                  data-testid="register-name-input"
                />
              </div>
            </div>
            <div>
              <label className="label">E-Mail</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="du@nordgaming.de"
                  className="input pl-10"
                  data-testid="register-email-input"
                />
              </div>
            </div>
            <div>
              <label className="label">Passwort (min. 6 Zeichen)</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="input pl-10"
                  data-testid="register-password-input"
                />
              </div>
            </div>
            {error && (
              <div
                className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 p-3 rounded-sm font-mono"
                data-testid="register-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full"
              data-testid="register-submit-button"
            >
              {submitting ? "Erstellen…" : (
                <>
                  Account erstellen
                  <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-sm text-white/60 text-center">
            Bereits Mitglied?{" "}
            <Link to="/login" className="text-primary link-underline" data-testid="go-to-login">
              Hier einloggen
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
